/**
 * Runs a connector target change as one transaction: the previously applied
 * target is removed before the requested one is written, and the setup revision
 * always learns what happened.
 */

import { useCallback, useEffect, useRef } from 'react';

import {
  buildConnectorTargetRequest,
  connectorTargetBlockMessage,
  connectorTargetRevisionEvent,
  connectorTargetRevisionFailure,
  resolveConnectorDocumentIdentity,
  runConnectorTargetTransaction,
} from '../../connectorTarget';
import { logSetupRevision } from '../../setupRevision';
import { collectConnectorSetupFields } from './connectorSetupFields';
import { createConnectorTargetPorts, readLiveConnectorTarget } from './connectorTargetPorts';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
import { useUIStore } from '../../../stores/useUIStore';
import { webLog } from '../../../api/grpc/webLogger';
import type { ConnectorTargetOutcome, ConnectorTargetRequest } from '../../connectorTarget';
import type {
  ConnectorTargetTransactionResult,
  ParameterAdditionOptions,
  ParameterAdditionResult,
} from './models';

export interface ConnectorTargetTransactionDeps {
  projectKey: string | null;
  documentName: string | null;
  documentPath: string | null;
  addParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  undoLastConnectorParameterAddition: () => Promise<boolean>;
}

export interface UseConnectorTargetTransactionReturn {
  applyConnectorTarget: (
    options?: ParameterAdditionOptions
  ) => Promise<ConnectorTargetTransactionResult>;
}

const unknownProjectOutcome = (): ConnectorTargetOutcome => ({
  kind: 'blocked',
  reason: 'unknown-project',
  message: connectorTargetBlockMessage('unknown-project'),
});

/** Freezes the request at click time, so nothing that changes later redirects it. */
const freezeRequest = (
  deps: ConnectorTargetTransactionDeps,
  target?: ParameterAdditionOptions['connectorTarget']
): ConnectorTargetRequest | null =>
  buildConnectorTargetRequest({
    projectKey: deps.projectKey,
    documentName: deps.documentName,
    documentPath: deps.documentPath,
    target: target ?? readLiveConnectorTarget(),
    setupRevisionId: useUIStore.getState().setupRevisionId,
    setupFields: collectConnectorSetupFields(
      useConfigurationStore.getState().connectorBindingConfiguration
    ),
  });

const reportToSetupRevision = (outcome: ConnectorTargetOutcome): void => {
  useUIStore.getState().applySetupRevisionEvent(connectorTargetRevisionEvent(outcome));
};

/**
 * Records where the revision ended up, reading the phase after the event has
 * landed. Only named fields are passed, so no profile content can reach the log.
 */
const logConnectorTargetOutcome = (outcome: ConnectorTargetOutcome): void => {
  const state = useUIStore.getState();
  const failure = connectorTargetRevisionFailure(outcome);

  logSetupRevision(failure ? 'revisionFailed' : 'phaseEntered', {
    revisionId: state.setupRevisionId,
    phase: state.setupRevisionStatus,
    projectKey: state.setupRevisionProjectKey,
    planId: state.connectorParameterUndoPlan?.id ?? null,
    failure,
  });
};

export const useConnectorTargetTransaction = (
  deps: ConnectorTargetTransactionDeps
): UseConnectorTargetTransactionReturn => {
  // Read through a ref, because the callback outlives the render it came from.
  const depsRef = useRef(deps);

  useEffect(() => {
    depsRef.current = deps;
  }, [deps]);

  const applyConnectorTarget = useCallback(
    async (options?: ParameterAdditionOptions): Promise<ConnectorTargetTransactionResult> => {
      const request = freezeRequest(depsRef.current, options?.connectorTarget);
      if (!request) {
        const outcome = unknownProjectOutcome();
        reportToSetupRevision(outcome);
        logConnectorTargetOutcome(outcome);
        return { outcome, applyResult: null };
      }

      webLog.info('parameterSetup', `connector target dispatch ${JSON.stringify({
        projectKey: request.projectKey,
        destination: request.target.destination,
        bindingKind: request.target.bindingKind,
        setupRevisionId: request.setupRevisionId,
      })}`);

      const applied: { result: ParameterAdditionResult | null } = { result: null };
      const ports = createConnectorTargetPorts({
        readIdentity: () => resolveConnectorDocumentIdentity(depsRef.current),
        addParameters: (mutationOptions) => depsRef.current.addParameters(mutationOptions),
        undoLastConnectorParameterAddition: () =>
          depsRef.current.undoLastConnectorParameterAddition(),
        options,
        onApplyResult: (result) => {
          applied.result = result;
        },
      });

      const outcome = await runConnectorTargetTransaction(request, ports);
      reportToSetupRevision(outcome);
      logConnectorTargetOutcome(outcome);

      return { outcome, applyResult: applied.result };
    },
    []
  );

  return { applyConnectorTarget };
};
